/**
 * use-keyboard-shortcuts
 *
 * Generic global keyboard-shortcut hook used by the command palette,
 * shortcuts-help modal, and page-level forms.
 *
 * Usage:
 *   useKeyboardShortcuts({
 *     "mod+k": () => openPalette(),
 *     "mod+n": () => openAddClient(),
 *     "?":     () => openHelp(),
 *     "mod+enter": () => submitForm(),
 *   });
 *
 * Notes:
 * - `mod` maps to Cmd on Mac and Ctrl elsewhere.
 * - Handlers do NOT fire when the user is typing in an <input>, <textarea>,
 *   or contenteditable element. Exception: combos that include `mod` still
 *   fire inside inputs (so Cmd+Enter to save works).
 * - Shortcut strings are case-insensitive.
 * - Pass `enabled: false` to temporarily disable all bindings.
 */
import { useEffect, useRef } from "react";

export type ShortcutMap = Record<string, (e: KeyboardEvent) => void>;

export interface UseKeyboardShortcutsOptions {
  /** When false, listeners are not attached. Default: true. */
  enabled?: boolean;
  /** Element to bind to; defaults to window. */
  target?: HTMLElement | Window | Document | null;
}

interface ParsedCombo {
  mod: boolean;
  shift: boolean;
  alt: boolean;
  key: string; // lowercase
}

function parseCombo(combo: string): ParsedCombo {
  const parts = combo
    .toLowerCase()
    .split("+")
    .map((p) => p.trim())
    .filter(Boolean);
  const out: ParsedCombo = { mod: false, shift: false, alt: false, key: "" };
  for (const p of parts) {
    if (p === "mod" || p === "cmd" || p === "ctrl" || p === "meta") out.mod = true;
    else if (p === "shift") out.shift = true;
    else if (p === "alt" || p === "option") out.alt = true;
    else out.key = p;
  }
  return out;
}

function isTypingInFormField(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
}

function matchCombo(e: KeyboardEvent, combo: ParsedCombo): boolean {
  // `mod` means Cmd on Mac, Ctrl elsewhere.
  const isMac =
    typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/i.test(navigator.platform);
  const modPressed = isMac ? e.metaKey : e.ctrlKey;

  if (combo.mod !== modPressed) return false;
  if (combo.shift !== e.shiftKey) return false;
  if (combo.alt !== e.altKey) return false;

  // Normalize key — cmdk/React may fire "?" as shift+/ so allow either.
  const key = e.key.toLowerCase();
  if (combo.key === "?") return key === "?" || (e.shiftKey && key === "/");
  if (combo.key === "enter") return key === "enter";
  if (combo.key === "escape" || combo.key === "esc") return key === "escape";

  return key === combo.key;
}

export function useKeyboardShortcuts(
  shortcuts: ShortcutMap,
  { enabled = true, target }: UseKeyboardShortcutsOptions = {}
): void {
  // Keep latest handlers without re-binding on every render.
  const handlersRef = useRef<ShortcutMap>(shortcuts);
  handlersRef.current = shortcuts;

  useEffect(() => {
    if (!enabled) return;
    const el: Window | Document | HTMLElement =
      (target as Window | Document | HTMLElement | null) ?? window;

    // Pre-parse once per effect.
    const parsed: Array<{ combo: ParsedCombo; raw: string }> = Object.keys(
      handlersRef.current
    ).map((raw) => ({ raw, combo: parseCombo(raw) }));

    const onKeyDown = (ev: Event) => {
      const e = ev as KeyboardEvent;
      const typing = isTypingInFormField(e.target);

      for (const { combo, raw } of parsed) {
        if (!matchCombo(e, combo)) continue;
        // Allow `mod`-combos even while typing (e.g. Cmd+Enter, Cmd+K).
        if (typing && !combo.mod) continue;
        const handler = handlersRef.current[raw];
        if (handler) handler(e);
        break;
      }
    };

    el.addEventListener("keydown", onKeyDown as EventListener);
    return () => el.removeEventListener("keydown", onKeyDown as EventListener);
  }, [enabled, target]);
}

export default useKeyboardShortcuts;
